/**
 * Lexical Editor Toolbar
 * Toolbar component for Lexical editor with AST integration
 */

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { $createHeadingNode, $isHeadingNode, HeadingTagType } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import {
  Code,
  ContentCopy,
  DataObject,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatIndentDecrease,
  FormatIndentIncrease,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatTextdirectionLToR,
  FormatTextdirectionRToL,
  FormatUnderlined,
  HorizontalRule,
  Image,
  Label,
  Link,
  Numbers,
  Redo,
  Save,
  Security,
  Undo,
} from '@mui/icons-material'
import { Box, ButtonGroup, Divider, FormControl, IconButton, MenuItem, Select, Tooltip } from '@mui/material'
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  LexicalCommand,
  LexicalEditor,
  LexicalNode,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  createCommand,
} from 'lexical'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { apiService } from '../../../services/api'
import {
  AstEditorCommands,
  AstUndoRedoManager,
  ContentAST,
  generateBlockId,
  generateEncryptionId,
} from '../../../transforms/ast'
import { logger } from '../../../utils/logger'
import { FONTS, FONT_SIZES } from '../editor.constants'
import LinkDialog from '../LinkDialog'
import {
  $createCustomDividerNode,
  $createCustomEncryptedNode,
  $createCustomImageNode,
  $selectionContainsEmbed,
} from './nodes'
import EncryptionKindDialog, { CharacterOption } from '../../encryption/EncryptionKindDialog'

// Custom commands for keyboard shortcuts and context menu
export const OPEN_LINK_DIALOG_COMMAND: LexicalCommand<void> = createCommand('OPEN_LINK_DIALOG_COMMAND')
export const MARK_ENCRYPTION_COMMAND: LexicalCommand<void> = createCommand('MARK_ENCRYPTION_COMMAND')
export const TAG_SELECTION_COMMAND: LexicalCommand<void> = createCommand('TAG_SELECTION_COMMAND')
/** Toggle the "strong moment" tag on the current selection — handler decides mark vs unmark. */
export const TOGGLE_MOMENT_COMMAND: LexicalCommand<void> = createCommand('TOGGLE_MOMENT_COMMAND')
/** Ask the authoring assistant about the current selection. */
export const ASK_ASSISTANT_COMMAND: LexicalCommand<void> = createCommand('ASK_ASSISTANT_COMMAND')
/** Open the dep dialog with the current section as the `before` endpoint (`current → ?`). */
export const ADD_DEP_BEFORE_COMMAND: LexicalCommand<void> = createCommand('ADD_DEP_BEFORE_COMMAND')
/** Open the dep dialog with the current section as the `after` endpoint (`? → current`). */
export const ADD_DEP_AFTER_COMMAND: LexicalCommand<void> = createCommand('ADD_DEP_AFTER_COMMAND')

// Helper functions for style parsing
function parseStyleString(styleString: string): Record<string, string> {
  const styleObj: Record<string, string> = {}
  if (!styleString) {
    return styleObj
  }

  styleString.split(';').forEach((rule) => {
    const [key, value] = rule.split(':').map((s) => s.trim())
    if (key && value) {
      styleObj[key] = value
    }
  })
  return styleObj
}

function stringifyStyleObject(styleObj: Record<string, string>): string {
  return Object.entries(styleObj)
    .filter(([_, value]) => value)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ')
}

export interface LexicalToolbarProps {
  editor: LexicalEditor
  editorCommands?: AstEditorCommands
  undoRedoManager?: AstUndoRedoManager
  onSave?: () => void
  saving?: boolean
  autoSaving?: boolean
  hasUnsavedChanges?: boolean
  recentlySaved?: boolean
  showSaveButton?: boolean
  currentDirection?: 'ltr' | 'rtl'
  onToggleDirection?: () => void
  onSetDirection?: (direction: 'ltr' | 'rtl') => void
  debugMode?: boolean
  onToggleDebug?: () => void
  showLineNumbers?: boolean
  onToggleLineNumbers?: () => void
  showWritingSyntax?: boolean
  onToggleWritingSyntax?: () => void
  onCopyDebugData?: () => void
  readOnly?: boolean
  className?: string
  onUndo?: () => void
  onRedo?: () => void
  onASTChange?: (ast: ContentAST) => void
  bookId?: string
  chapterId?: string
}

/**
 * Toolbar for Lexical editor
 */
export const LexicalToolbar: React.FC<LexicalToolbarProps> = ({
  editor,
  editorCommands: _editorCommands,
  undoRedoManager,
  onSave,
  saving,
  autoSaving: _autoSaving,
  hasUnsavedChanges,
  recentlySaved = false,
  showSaveButton = true,
  currentDirection = 'ltr',
  onToggleDirection,
  onSetDirection,
  debugMode = false,
  onToggleDebug,
  showLineNumbers = false,
  onToggleLineNumbers,
  showWritingSyntax = false,
  onToggleWritingSyntax,
  onCopyDebugData,
  readOnly = false,
  className,
  onUndo,
  onRedo,
  onASTChange: _onASTChange,
  bookId,
}) => {
  // Format state
  const [encryptionKindOpen, setEncryptionKindOpen] = useState(false)
  const [characters, setCharacters] = useState<CharacterOption[]>([])
  useEffect(() => {
    if (!encryptionKindOpen || !bookId) {
      return
    }
    apiService
      .listCharacters(bookId)
      .then(setCharacters)
      .catch(() => setCharacters([]))
  }, [encryptionKindOpen, bookId])

  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [blockType, setBlockType] = useState<string>('paragraph')
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  const [currentFont, setCurrentFont] = useState('Arial')
  const [currentSize, setCurrentSize] = useState('12')
  const [hasSelection, setHasSelection] = useState(false)
  const [blockDirection, setBlockDirection] = useState<'ltr' | 'rtl' | null>(null)

  // Link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkDialogMode, setLinkDialogMode] = useState<'create' | 'edit'>('create')
  const [currentLinkData, setCurrentLinkData] = useState<{ url: string; text: string } | null>(null)
  const [isOnLink, setIsOnLink] = useState(false)

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check if a node or its ancestors are a link
  const findLinkAncestor = useCallback((node: LexicalNode): { url: string; text: string } | null => {
    let current: LexicalNode | null = node
    while (current !== null) {
      if ($isLinkNode(current)) {
        const url = current.getURL()
        const text = current.getTextContent()
        return { url, text }
      }
      current = current.getParent()
    }
    return null
  }, [])

  // Update toolbar state based on selection
  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) {
        setHasSelection(false)
        return
      }

      // Check if selection has content (not collapsed)
      setHasSelection(!selection.isCollapsed())

      // Update text format states
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))

      // Update block type
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      if ($isHeadingNode(element)) {
        setBlockType(element.getTag())
      } else if ($isListNode(element)) {
        const parentList = element
        setBlockType(parentList.getListType() === 'number' ? 'ol' : 'ul')
      } else {
        const elementType = element.getType()
        setBlockType(elementType)
      }

      setBlockDirection($isElementNode(element) ? element.getDirection() : null)

      // Update link state
      const linkData = findLinkAncestor(anchorNode)
      if (linkData) {
        setIsOnLink(true)
        setCurrentLinkData(linkData)
      } else {
        setIsOnLink(false)
        if (!linkDialogOpen) {
          setCurrentLinkData(null)
        }
      }
    })
  }, [editor, findLinkAncestor, linkDialogOpen])

  // Update undo/redo state
  useEffect(() => {
    if (undoRedoManager) {
      const updateUndoRedo = () => {
        const state = undoRedoManager.getState()
        setCanUndo(state.canUndo)
        setCanRedo(state.canRedo)
      }
      updateUndoRedo()
      // Update on interval or when needed
      const interval = setInterval(updateUndoRedo, 500)
      return () => clearInterval(interval)
    }
  }, [undoRedoManager])

  // Listen for selection changes
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  // Format handlers
  const formatBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
  }, [editor])

  const formatItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
  }, [editor])

  const formatUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
  }, [editor])

  // Block type handlers
  const formatParagraph = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection) && !$selectionContainsEmbed(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }, [editor])

  const formatHeading = useCallback(
    (headingSize: HeadingTagType) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection) && !$selectionContainsEmbed(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize))
        }
      })
    },
    [editor],
  )

  // List handlers
  const formatBulletList = useCallback(() => {
    if (blockType === 'ul') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    }
  }, [editor, blockType])

  const formatNumberedList = useCallback(() => {
    if (blockType === 'ol') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }
  }, [editor, blockType])

  // Indent handlers
  const formatIndent = useCallback(() => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
  }, [editor])

  const formatOutdent = useCallback(() => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
  }, [editor])

  // Block style change handler
  const handleBlockStyleChange = useCallback(
    (value: string) => {
      switch (value) {
        case 'paragraph':
          formatParagraph()
          break
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          formatHeading(value as HeadingTagType)
          break
      }
    },
    [formatParagraph, formatHeading],
  )

  // Text alignment handlers
  const formatAlignLeft = useCallback(() => {
    editor.focus()
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
  }, [editor])

  const formatAlignCenter = useCallback(() => {
    editor.focus()
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
  }, [editor])

  const formatAlignRight = useCallback(() => {
    editor.focus()
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
  }, [editor])

  // Divider handler
  const insertDivider = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const blockId = generateBlockId()
        const dividerNode = $createCustomDividerNode(blockId)
        $insertNodes([dividerNode])
      }
    })
  }, [editor])

  // Link handlers
  const openLinkDialog = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode()
        const linkData = findLinkAncestor(anchorNode)

        if (linkData) {
          // Editing existing link
          setLinkDialogMode('edit')
          setCurrentLinkData(linkData)
        } else {
          // Creating new link
          setLinkDialogMode('create')
          // Get selected text as initial link text
          const selectedText = selection.getTextContent()
          setCurrentLinkData(selectedText ? { url: '', text: selectedText } : null)
        }
        setLinkDialogOpen(true)
      }
    })
  }, [editor, findLinkAncestor])

  // Listen for open link dialog command (from keyboard shortcut)
  useEffect(() => {
    return editor.registerCommand(
      OPEN_LINK_DIALOG_COMMAND,
      () => {
        openLinkDialog()
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [editor, openLinkDialog])

  const handleLinkConfirm = useCallback(
    (url: string, text: string) => {
      editor.update(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          return
        }

        if (linkDialogMode === 'edit') {
          // Update existing link - need to find the link node and update both URL and text
          const anchorNode = selection.anchor.getNode()
          let linkNode: LexicalNode | null = anchorNode

          // Find the link node in ancestors
          while (linkNode !== null && !$isLinkNode(linkNode)) {
            linkNode = linkNode.getParent()
          }

          if (linkNode && $isLinkNode(linkNode)) {
            // Update the URL
            linkNode.setURL(url)

            // Update the text content - find the first text node and update it
            const children = linkNode.getChildren()
            if (children.length > 0) {
              const firstChild = children[0]
              if ($isTextNode(firstChild)) {
                firstChild.setTextContent(text)
              }
            }
          }
        } else {
          // Create new link
          if (selection.isCollapsed()) {
            // No selection - insert link with text
            const textNode = $createTextNode(text)
            $insertNodes([textNode])
            textNode.select()
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
          } else {
            // Has selection - wrap in link
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
          }
        }
      })
      setLinkDialogOpen(false)
      setCurrentLinkData(null)
      editor.focus()
    },
    [editor, linkDialogMode],
  )

  const handleLinkRemove = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    setLinkDialogOpen(false)
    setCurrentLinkData(null)
    editor.focus()
  }, [editor])

  // Image upload handlers
  const handleImageUpload = useCallback(() => {
    if (!bookId) {
      toast.error('Cannot upload image: book ID not available')
      return
    }
    fileInputRef.current?.click()
  }, [bookId])

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file || !bookId) {
        return
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.')
        return
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024
      if (file.size > maxSize) {
        toast.error('File size exceeds 10MB limit')
        return
      }

      setUploadingImage(true)
      const uploadToast = toast.loading('Uploading image...')

      try {
        const response = await apiService.uploadBookImage(bookId, file)

        if (response.success && response.data) {
          const imageUrl = response.data.url

          editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              const blockId = generateBlockId()
              const imageNode = $createCustomImageNode(blockId, imageUrl, file.name)
              $insertNodes([imageNode])

              // Add a paragraph after the image for continued editing
              const paragraphNode = $createParagraphNode()
              $insertNodes([paragraphNode])
            }
          })

          toast.dismiss(uploadToast)
        } else {
          toast.error(response.message || 'Failed to upload image', { id: uploadToast })
        }
      } catch (error: any) {
        logger.error('Error uploading image:', error)
        toast.error(error.message || 'Failed to upload image', { id: uploadToast })
      } finally {
        setUploadingImage(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [editor, bookId],
  )

  // Font handlers
  const handleFontChange = useCallback(
    (value: string) => {
      setCurrentFont(value)
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          // Apply font family via inline style
          selection.getNodes().forEach((node) => {
            if ($isTextNode(node)) {
              const currentStyle = node.getStyle()
              // Parse existing styles and add/update font-family
              const styleObj = parseStyleString(currentStyle)
              styleObj['font-family'] = value
              node.setStyle(stringifyStyleObject(styleObj))
            }
          })
        }
      })
    },
    [editor],
  )

  const handleFontSizeChange = useCallback(
    (value: string) => {
      setCurrentSize(value)
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          // Apply font size via inline style
          selection.getNodes().forEach((node) => {
            if ($isTextNode(node)) {
              const currentStyle = node.getStyle()
              // Parse existing styles and add/update font-size
              const styleObj = parseStyleString(currentStyle)
              styleObj['font-size'] = `${value}pt`
              node.setStyle(stringifyStyleObject(styleObj))
            }
          })
        }
      })
    },
    [editor],
  )

  // Handle encryption marking
  const handleMarkEncryption = useCallback(
    (character?: string, phrase?: string, authorHeld?: boolean) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($selectionContainsEmbed(selection)) {
          toast.error('יש לבחור טקסט לעריכה מחוץ לשיבוץ')
          return
        }
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          logger.warn('No text selected to encrypt')
          return
        }

        // Get selected text
        const selectedText = selection.getTextContent()
        if (!selectedText || !selectedText.trim()) {
          toast.error('Cannot encrypt empty or whitespace-only content')
          return
        }

        // Generate encryption ID
        const encryptionId = generateEncryptionId()

        // Get the anchor and focus points
        const anchor = selection.anchor
        const focus = selection.focus

        // Determine which is first (for proper ordering)
        const isBackward = selection.isBackward()
        const startPoint = isBackward ? focus : anchor
        const endPoint = isBackward ? anchor : focus

        // Get the nodes in the selection
        const nodes = selection.getNodes()

        if (nodes.length === 0) {
          toast.error('No content selected')
          return
        }

        // For single text node selection (most common case)
        if (nodes.length === 1 && $isTextNode(nodes[0])) {
          const textNode = nodes[0]
          const startOffset = startPoint.offset
          const endOffset = endPoint.offset

          // Get the text content
          const fullText = textNode.getTextContent()
          const selectedPortion = fullText.slice(startOffset, endOffset)

          if (!selectedPortion.trim()) {
            toast.error('Cannot encrypt empty or whitespace-only content')
            return
          }

          // Create encrypted node with the selected text stored directly
          const encryptedNode = $createCustomEncryptedNode(
            encryptionId,
            selectedPortion,
            'aes-gcm',
            1,
            character,
            authorHeld ? true : undefined,
          )

          // Now modify the original text node
          if (startOffset === 0 && endOffset === fullText.length) {
            // Entire node selected - replace it
            textNode.replace(encryptedNode)
          } else if (startOffset === 0) {
            // Selection starts at beginning
            textNode.setTextContent(fullText.slice(endOffset))
            textNode.insertBefore(encryptedNode)
          } else if (endOffset === fullText.length) {
            // Selection ends at end
            textNode.setTextContent(fullText.slice(0, startOffset))
            textNode.insertAfter(encryptedNode)
          } else {
            // Selection is in the middle - split into three parts
            const afterText = fullText.slice(endOffset)
            textNode.setTextContent(fullText.slice(0, startOffset))

            // Create the after text node
            const afterNode = $createTextNode(afterText)
            afterNode.setFormat(textNode.getFormat())
            afterNode.setStyle(textNode.getStyle())

            textNode.insertAfter(encryptedNode)
            encryptedNode.insertAfter(afterNode)
          }
        } else {
          // Multi-node selection - combine all text and create single encrypted node.
          // This merges paragraphs into one block intentionally — readers must not be
          // able to infer paragraph structure from the encrypted output.
          // Build the text manually: \n\n between block elements, \n for line breaks,
          // so decrypt can restore paragraph structure.
          const selectedNodes = selection.getNodes()
          const parts: string[] = []
          let lastParentKey: string | null = null
          for (const node of selectedNodes) {
            const parent = node.getTopLevelElement()
            const parentKey = parent?.getKey() ?? null
            if (parentKey !== lastParentKey && lastParentKey !== null) {
              // New block boundary → paragraph separator
              parts.push('\n\n')
            }
            lastParentKey = parentKey
            if ($isTextNode(node)) {
              parts.push(node.getTextContent())
            } else if (node.getType() === 'linebreak') {
              parts.push('\n')
            } else if ($isElementNode(node)) {
              // Skip block elements themselves — we handle their children
            } else {
              parts.push(node.getTextContent())
            }
          }
          const combinedText = parts.join('')
          const encryptedNode = $createCustomEncryptedNode(
            encryptionId,
            combinedText,
            'aes-gcm',
            1,
            character,
            authorHeld ? true : undefined,
          )

          // Remove selected content and insert encrypted node
          selection.removeText()
          $insertNodes([encryptedNode])
        }

        logger.debug('✅ Marked selection as encrypted:', encryptionId)

        // The sentence lives against this passage's id, not on the book: different
        // passages may be locked behind different sentences.
        if (phrase && bookId) {
          apiService.saveLockPhrase(bookId, encryptionId, phrase).catch((err) => {
            logger.error('Failed to save the lock phrase', err)
            toast.error('הקטע סומן, אבל המשפט לא נשמר')
          })
        }
      })
    },
    [editor, bookId],
  )

  // Handle tag selection (text range, block, or cursor context)
  const handleTagSelection = useCallback(() => {
    editor.dispatchCommand(TAG_SELECTION_COMMAND, undefined)
  }, [editor])

  // Listen for mark encryption command (from keyboard shortcut / context menu)
  useEffect(() => {
    return editor.registerCommand(
      MARK_ENCRYPTION_COMMAND,
      () => {
        setEncryptionKindOpen(true)
        return true
      },
      COMMAND_PRIORITY_LOW,
    )
  }, [editor])

  // Handle undo
  const handleUndo = useCallback(() => {
    if (onUndo) {
      onUndo()
    } else {
      editor.dispatchCommand(UNDO_COMMAND, undefined)
    }
  }, [editor, onUndo])

  // Handle redo
  const handleRedo = useCallback(() => {
    if (onRedo) {
      onRedo()
    } else {
      editor.dispatchCommand(REDO_COMMAND, undefined)
    }
  }, [editor, onRedo])

  // Toolbar groups use responsive `order` so mobile re-arranges the bar:
  // Undo/Redo · Format · Align · Link/Image · Lists · HR · Block Style · Font · Tag/Encryption · Direction
  const groupSx = (xsOrder: number, mdOrder: number) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    order: { xs: xsOrder, md: mdOrder },
    '& > *': { flex: '0 0 auto' },
  })

  return (
    <Box
      className={className}
      dir="ltr"
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 0.5,
        padding: { xs: '4px 8px', md: '8px 16px' },
        backgroundColor: 'var(--toolbar-bg)',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        color: 'var(--icon-default)',
        overflowX: 'auto',
        overflowY: 'hidden',
        whiteSpace: 'nowrap',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        // Toolbar is a control row, not content — the icon `order` values are designed for
        // LTR flow. Forcing LTR keeps the layout stable when the toolbar is rendered inside
        // an RTL content surface (e.g. the OrganizeChapters section preview dialog).
        direction: 'ltr',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '& > *': {
          flex: '0 0 auto',
        },
        '& .MuiIconButton-root': {
          color: 'var(--icon-default)',
        },
        '& .MuiIconButton-root:hover': {
          color: readOnly ? undefined : 'var(--icon-hover)',
          bgcolor: readOnly ? undefined : 'var(--bg-hover)',
        },
        '& .MuiIconButton-root.Mui-disabled': {
          color: 'var(--text-tertiary)',
        },
        '& .MuiFormControl-root .MuiInputBase-root': {
          color: 'var(--text-primary)',
        },
        '& .MuiFormControl-root .MuiSvgIcon-root': {
          color: 'var(--icon-default)',
        },
        '& .MuiButtonGroup-root': {
          flex: '0 0 auto',
        },
        '& .MuiDivider-root': {
          flex: '0 0 auto',
        },
        ...(readOnly && {
          opacity: 0.35,
          pointerEvents: 'none',
        }),
      }}
    >
      {/* Group: Undo/Redo */}
      <Box sx={groupSx(0, 0)}>
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Undo (Ctrl+Z)">
            <span>
              <IconButton size="small" onClick={handleUndo} disabled={!canUndo}>
                <Undo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Shift+Z)">
            <span>
              <IconButton size="small" onClick={handleRedo} disabled={!canRedo}>
                <Redo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </ButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Text Formatting (Bold/Italic/Underline) */}
      <Box sx={groupSx(1, 3)}>
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Bold (Ctrl+B)">
            <IconButton size="small" onClick={formatBold} color={isBold ? 'primary' : 'default'}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic (Ctrl+I)">
            <IconButton size="small" onClick={formatItalic} color={isItalic ? 'primary' : 'default'}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline (Ctrl+U)">
            <IconButton size="small" onClick={formatUnderline} color={isUnderline ? 'primary' : 'default'}>
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Alignment */}
      <Box sx={groupSx(2, 4)}>
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Align Left">
            <IconButton size="small" onClick={formatAlignLeft}>
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton size="small" onClick={formatAlignCenter}>
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton size="small" onClick={formatAlignRight}>
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Link + Image */}
      <Box sx={groupSx(3, 5)}>
        <Tooltip title={isOnLink ? 'Edit Link (⌘K)' : 'Insert Link (⌘K)'}>
          <IconButton
            size="small"
            onClick={openLinkDialog}
            onMouseDown={(e) => e.preventDefault()}
            color={isOnLink ? 'primary' : 'default'}
          >
            <Link fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Image">
          <span>
            <IconButton size="small" onClick={handleImageUpload} disabled={uploadingImage || !bookId || readOnly}>
              <Image fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Lists */}
      <Box sx={groupSx(4, 6)}>
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Bullet List (⌘⇧8)">
            <IconButton size="small" onClick={formatBulletList} color={blockType === 'ul' ? 'primary' : 'default'}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List (⌘⇧7)">
            <IconButton size="small" onClick={formatNumberedList} color={blockType === 'ol' ? 'primary' : 'default'}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Decrease Indent (⇧Tab)">
            <IconButton size="small" onClick={formatOutdent}>
              <FormatIndentDecrease
                fontSize="small"
                sx={(blockDirection ?? currentDirection) === 'rtl' ? { transform: 'scaleX(-1)' } : undefined}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Increase Indent (Tab)">
            <IconButton size="small" onClick={formatIndent}>
              <FormatIndentIncrease
                fontSize="small"
                sx={(blockDirection ?? currentDirection) === 'rtl' ? { transform: 'scaleX(-1)' } : undefined}
              />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Horizontal Rule */}
      <Box sx={groupSx(5, 7)}>
        <Tooltip title="Horizontal Rule">
          <IconButton size="small" onClick={insertDivider}>
            <HorizontalRule fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Block Style Selector */}
      <Box sx={groupSx(6, 1)}>
        <Tooltip title="Text style (⌘⌥0-5)">
          <FormControl size="small" sx={{ minWidth: { xs: 110, md: 120 } }}>
            <Select
              value={['paragraph', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(blockType) ? blockType : 'paragraph'}
              onChange={(e) => handleBlockStyleChange(e.target.value)}
              sx={{ fontSize: '0.875rem', height: 32, color: 'var(--text-primary)' }}
            >
              <MenuItem value="paragraph">Normal text (⌘⌥0)</MenuItem>
              <MenuItem value="h1">Title (⌘⌥1)</MenuItem>
              <MenuItem value="h2">Subtitle (⌘⌥2)</MenuItem>
              <MenuItem value="h3">Heading 1 (⌘⌥3)</MenuItem>
              <MenuItem value="h4">Heading 2 (⌘⌥4)</MenuItem>
              <MenuItem value="h5">Heading 3 (⌘⌥5)</MenuItem>
              <MenuItem value="h6">Heading 4</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Font + Font Size */}
      <Box sx={groupSx(7, 2)}>
        <FormControl size="small" sx={{ minWidth: { xs: 90, md: 100 } }}>
          <Select
            value={currentFont}
            onChange={(e) => handleFontChange(e.target.value)}
            sx={{ fontSize: '0.875rem', height: 32, color: 'var(--text-primary)' }}
          >
            {FONTS.map((font) => (
              <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <Select
            value={currentSize}
            onChange={(e) => handleFontSizeChange(e.target.value)}
            sx={{ fontSize: '0.875rem', height: 32, color: 'var(--text-primary)' }}
          >
            {FONT_SIZES.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Tag + Encryption */}
      <Box sx={groupSx(8, 8)}>
        <Tooltip title="Tag Selection (Ctrl+Alt+T)">
          <IconButton onClick={handleTagSelection} onMouseDown={(e) => e.preventDefault()} size="small">
            <Label fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mark for Encryption (⌘⌥E)">
          <span>
            <IconButton
              onClick={() => setEncryptionKindOpen(true)}
              onMouseDown={(e) => e.preventDefault()}
              size="small"
              disabled={!hasSelection}
            >
              <Security fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      </Box>

      {/* Group: Direction */}
      <Box sx={groupSx(9, 9)}>
        <ButtonGroup size="small" variant="text">
          <Tooltip title="Left-to-Right">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (onSetDirection ? onSetDirection('ltr') : onToggleDirection?.())}
              color={blockDirection === 'ltr' ? 'primary' : 'default'}
            >
              <FormatTextdirectionLToR fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Right-to-Left">
            <IconButton
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (onSetDirection ? onSetDirection('rtl') : onToggleDirection?.())}
              color={blockDirection === 'rtl' ? 'primary' : 'default'}
            >
              <FormatTextdirectionRToL fontSize="small" />
            </IconButton>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: { xs: '0 0 8px', md: 1 }, order: 10 }} />

      {/* Line Numbers Toggle */}
      {onToggleWritingSyntax && (
        <Tooltip title={showWritingSyntax ? 'הסתר סימני תחביר כתיבה' : 'הצג סימני תחביר כתיבה'}>
          <IconButton
            size="small"
            aria-label="הצג תחביר"
            aria-pressed={showWritingSyntax}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onToggleWritingSyntax}
            color={showWritingSyntax ? 'primary' : 'default'}
            sx={{ order: 11 }}
          >
            <DataObject fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onToggleLineNumbers && (
        <Tooltip title={showLineNumbers ? 'Hide line numbers' : 'Show line numbers'}>
          <IconButton
            size="small"
            onClick={onToggleLineNumbers}
            color={showLineNumbers ? 'primary' : 'default'}
            sx={{ order: 11 }}
          >
            <Numbers fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Debug Mode Toggle */}
      {onToggleDebug && (
        <Tooltip title={debugMode ? 'Hide Debug Panel' : 'Show Debug Panel'}>
          <IconButton size="small" onClick={onToggleDebug} color={debugMode ? 'primary' : 'default'} sx={{ order: 11 }}>
            <Code fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Copy Debug Data */}
      {debugMode && onCopyDebugData && (
        <Tooltip title="Copy Debug Data">
          <IconButton size="small" onClick={onCopyDebugData} sx={{ order: 12 }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {showSaveButton && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, order: 13, '& > *': { flex: '0 0 auto' } }}>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Save Button */}
          <Tooltip
            title={hasUnsavedChanges ? 'Save (Ctrl+S) - Unsaved changes' : recentlySaved ? 'Saved' : 'Save (Ctrl+S)'}
          >
            <span>
              <IconButton
                size="small"
                onClick={onSave}
                disabled={saving}
                color={hasUnsavedChanges ? 'warning' : recentlySaved ? 'success' : 'default'}
              >
                <Save fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Link Dialog */}
      <LinkDialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false)
          setCurrentLinkData(null)
          setLinkDialogMode('create')
          editor.focus()
        }}
        onConfirm={handleLinkConfirm}
        onRemove={linkDialogMode === 'edit' ? handleLinkRemove : undefined}
        initialUrl={currentLinkData?.url}
        initialText={currentLinkData?.text}
        mode={linkDialogMode}
      />

      <EncryptionKindDialog
        open={encryptionKindOpen}
        characters={characters}
        onClose={() => setEncryptionKindOpen(false)}
        onCreateCharacter={
          bookId
            ? async (name) => {
                const created = await apiService.createCharacter(bookId, name)
                setCharacters((prev) =>
                  prev.some((c) => c.tagId === created.tagId) ? prev : [...prev, { ...created }],
                )
                return created
              }
            : undefined
        }
        onConfirm={({ character, phrase, authorHeld }) => {
          setEncryptionKindOpen(false)
          handleMarkEncryption(character, phrase, authorHeld)
        }}
      />
    </Box>
  )
}

export default LexicalToolbar
